/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesAdministrativeLaw";
import personFields from "../modules/personFields";
import { dateFieldDefault, instituteReferenceDefault } from "../../../validationRules";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { abstractLitigationStageFormDefinition } from "../abstractLitigationCase/stageFormDefinition";
import DateChangeButtonsToolbar from "../../../../components/Validator/DateChangeButtonsToolbar";
import { CivilServiceLaw } from "../../../../types/Products/AdministrativeLaw/CivilServiceLaw/CivilServiceLaw";
import { cloneFieldsWithOverrides, insertAfterPath, removeByPath } from "../../../formServiceFunctions";
import { STATUS_ADMINISTRATIVE_DECISION_ISSUED } from "../../../LeadStatus/StatusAdministrativeLawCase";
import _ from "lodash";

export function stageFormDefinition(product: CivilServiceLaw): ReturnType<FormDefinition<CivilServiceLaw>> {
  const createdFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
  ];

  function dataCompletedFields(product: CivilServiceLaw): FormElementPropsWithoutProduct<CivilServiceLaw>[] {
    let fields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.personalTextMessage`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
        },
      },
      ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED],
    ];

    fields = removeByPath(fields, `${product.productClassName}.extrajudicialCommunicationDeadline`);

    return fields;
  }

  function dataLawFirmCompletedFields(product: CivilServiceLaw): FormElementPropsWithoutProduct<CivilServiceLaw>[] {
    let fields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [...dataCompletedFields(product)];

    return insertAfterPath(
      fields,
      [
        {
          type: "ValidatorDateField",
          path: `${product.productClassName}.administrativeDecisionReceivedDate`,
          validators: dateFieldDefault,
        },
      ],
      `${product.productClassName}.extrajudicialCommunicationDeadline`
    );
  }

  function checkedFields(product: CivilServiceLaw): FormElementPropsWithoutProduct<CivilServiceLaw>[] {
    let fields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
      {
        type: "ExpandableTextField",
        path: `${product.productClassName}.personalTextMessage`,
        isDisabled: () => true,
        additionalProps: {
          multiline: true,
        },
      },
      ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_CHECKED],
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.administrativeDecisionReceivedDate`,
        validators: dateFieldDefault,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.administrativeProceedingsLetterSentDeadline`,
        validators: dateFieldDefault,
        isHidden: ({ product }) => product.hasReceivedAdministrativeDecision,
      },
      {
        type: "ValidatorDateField",
        path: `${product.productClassName}.administrativeProceedingsLetterSentDate`,
        validators: dateFieldDefault,
        isHidden: ({ product }) => product.hasReceivedAdministrativeDecision,
      },
      {
        type: "ValidatorSelect",
        path: `${product.productClassName}.administrativeDecisionRecommendation`,
        isDisabled: () => false,
        isHidden: ({ product }) => product.advisoryHandling,
        options: () => [{ value: "objection" }, { value: "accept" }, { value: "individualText" }],
      },
      {
        type: "ValidatorTextField",
        path: `${product.productClassName}.administrativeDecisionRecommendationText`,
        isDisabled: () => false,
        isHidden: ({ product }) =>
          !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) ||
          (product.stage > stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED &&
            !product.administrativeDecisionRecommendationText),
        additionalProps: {
          multiline: true,
          fullWidth: true,
        },
      },
    ];

    fields = removeByPath(fields, `${product.productClassName}.extrajudicialCommunicationSent`);
    fields = removeByPath(fields, `${product.productClassName}.extrajudicialCommunicationDeadline`);

    return fields;
  }

  const administrativeProceedingsFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.administrativeDecisionReceivedDate`,
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.administrativeProceedingsLetterSentDeadline`,
      validators: dateFieldDefault,
      isDisabled: () => true,
      isHidden: ({ product }) => !product.administrativeProceedingsLetterSentDate,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.administrativeProceedingsLetterSentDate`,
      validators: dateFieldDefault,
      isDisabled: () => true,
      isHidden: ({ product }) => !product.administrativeProceedingsLetterSentDate,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.administrativeDecisionRecommendation`,
      isDisabled: () => false,
      isHidden: ({ product }) => product.advisoryHandling,
      options: () => [{ value: "objection" }, { value: "accept" }, { value: "individualText" }],
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.administrativeDecisionRecommendationText`,
      isDisabled: () => false,
      isHidden: ({ product }) =>
        !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) ||
        (product.stage > stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED &&
          !product.administrativeDecisionRecommendationText),
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
  ];

  const administrativeDecisionIssued: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.administrativeDecisionRecommendation`,
      isDisabled: ({ product }) =>
        product.stage < stagesCase.STAGE_DATA_COMPLETED ||
        product.stage >= stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED,
      isHidden: ({ product }) => product.advisoryHandling,
      options: () => [{ value: "objection" }, { value: "accept" }, { value: "individualText" }],
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.administrativeDecisionRecommendationText`,
      isDisabled: ({ product }) =>
        !(
          product.stage === stagesCase.STAGE_ADMINISTRATIVE_PROCEEDINGS ||
          (product.stage === stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED &&
            product.administrativeDecisionRecommendationText)
        ),
      isHidden: ({ product }) =>
        !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) ||
        (product.stage > stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED &&
          !product.administrativeDecisionRecommendationText),
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.administrativeDecisionStatusClient`,
      isDisabled: ({ product }) => product.leadStatus !== STATUS_ADMINISTRATIVE_DECISION_ISSUED,
      options: () => [{ value: "fullyAgreed" }, { value: "partiallyAgreed" }, { value: "notAgreed" }],
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.administrativeDecisionFeedbackClient`,
      isDisabled: ({ product }) => product.leadStatus !== STATUS_ADMINISTRATIVE_DECISION_ISSUED,
      options: () => [{ value: "accept" }, { value: "objection" }],
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationSent`,
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
      validators: dateFieldDefault,
      additionalProps: {
        toolbar: DateChangeButtonsToolbar,
      },
    },
  ];

  const inProgressFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_IN_PROGRESS],
  ];

  const communicationSentFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT],
  ];

  const reactionReceivedFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED],
  ];

  function actionRequiredFields(product: CivilServiceLaw): FormElementPropsWithoutProduct<CivilServiceLaw>[] {
    const actionRequiredFields = abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_ACTION_REQUIRED];

    if (product.administrativeCaseType === "main") {
      return actionRequiredFields;
    }

    return cloneFieldsWithOverrides<CivilServiceLaw>(actionRequiredFields, [
      {
        path: `${product.productClassName}.valueInDisputeExtrajudicialPreliminary`,
        patch: {
          isHidden: ({ product }) => product.administrativeCaseType !== "main",
        },
      },
      {
        path: `${product.productClassName}.subtractExtrajudicialProcessFee`,
        patch: {
          isHidden: ({ product }) => product.administrativeCaseType !== "main",
        },
      },
      {
        path: `${product.productClassName}.valueInDisputeExtrajudicialFinal`,
        patch: {
          isHidden: ({ product }) => product.administrativeCaseType !== "main",
        },
      },
      {
        path: `${product.productClassName}.costBearingByOpponentExtrajudicial`,
        patch: {
          isHidden: ({ product }) => product.administrativeCaseType !== "main",
        },
      },
    ]);
  }

  function prepareLawsuitFields(product: CivilServiceLaw): FormElementPropsWithoutProduct<CivilServiceLaw>[] {
    const prepareLawsuitFields = abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_PREPARE_LAWSUIT];

    if (product.administrativeCaseType === "main") {
      return prepareLawsuitFields;
    }

    return cloneFieldsWithOverrides<CivilServiceLaw>(prepareLawsuitFields, [
      {
        path: `${product.productClassName}.statementOfClaimSentDate`,
        patch: {
          translationPath: `${product.productClassName}.statementOfClaimSentDate.${product.administrativeCaseType}`,
        },
      },
    ]);
  }

  function lawsuitFields(product: CivilServiceLaw): FormElementPropsWithoutProduct<CivilServiceLaw>[] {
    const lawsuitFields = abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT];

    if (product.administrativeCaseType === "main" || product.administrativeCaseType === "failureToActLawsuit") {
      return lawsuitFields;
    }

    return cloneFieldsWithOverrides(lawsuitFields, [
      {
        path: `${product.productClassName}.statementOfClaimSentDate`,
        patch: {
          translationPath: `${product.productClassName}.statementOfClaimSentDate.${product.administrativeCaseType}`,
        },
      },
      {
        path: `${product.productClassName}.statementOfDefenseFiledDate`,
        patch: {
          translationPath: `${product.productClassName}.statementOfDefenseFiledDate.${product.administrativeCaseType}`,
        },
      },
      {
        path: `${product.productClassName}.judgementReceivedDate`,
        patch: {
          translationPath: `${product.productClassName}.judgementReceivedDate.${product.administrativeCaseType}`,
        },
      },
      {
        path: `${product.productClassName}.lawsuitCompletedReason`,
        patch: {
          translationPath: `${product.productClassName}.lawsuitCompletedReason.${product.administrativeCaseType}`,
          options: () => [
            { value: "order" },
            { value: "grant" },
            { value: "dismissal" },
            { value: "partialGrant" },
            { value: "lawsuitRevocation" },
            { value: "resolution" },
            { value: "settlement" },
          ],
        },
      },
    ]);
  }

  const lawsuitCompletedFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courtReference`,
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.judgementEebSentDate`,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.promulgationOfJudgementDate`,
      validators: dateFieldDefault,
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT_COMPLETED],
  ];

  const completedFields: FormElementPropsWithoutProduct<CivilServiceLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_COMPLETED],
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields(product),
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields(product),
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields(product),
    [stagesCase.STAGE_CHECKED]: checkedFields(product),
    [stagesCase.STAGE_IN_PROGRESS]: inProgressFields,
    [stagesCase.STAGE_ADMINISTRATIVE_PROCEEDINGS]: administrativeProceedingsFields,
    [stagesCase.STAGE_ADMINISTRATIVE_DECISION_ISSUED]: administrativeDecisionIssued,
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: communicationSentFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: reactionReceivedFields,
    [stagesCase.STAGE_ACTION_REQUIRED]: actionRequiredFields(product),
    [stagesCase.STAGE_PREPARE_LAWSUIT]: prepareLawsuitFields(product),
    [stagesCase.STAGE_LAWSUIT]: lawsuitFields(product),
    [stagesCase.STAGE_TRIAL]: lawsuitFields(product),
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]:
      abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EVALUATE_JUDGEMENT],
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: lawsuitCompletedFields,
    [stagesCase.STAGE_COMPLETED]: completedFields,
  };
}
